import React, { useState, useEffect } from 'react'
import { Cpu, Play, ChevronDown, ChevronUp, Code } from 'lucide-react'
import { mcpApi } from '../utils/api'

const TOOL_PRESETS: Record<string, { desc: string; params: Record<string, any> }> = {
  analyze_student_answer: {
    desc: '학생 답안 분석 (오개념 감지 + 피드백)',
    params: { student_id: 'student_001', step_id: 'LS05', score: 0.6, intercept_as_slope: true },
  },
  get_step_explanation: {
    desc: '학습 단계 설명 조회',
    params: { step_id: 'LS05' },
  },
  evaluate_linear_function: {
    desc: '1차 함수 속성 평가',
    params: { slope: -2, intercept: 3 },
  },
  get_misconception_feedback: {
    desc: '오개념 피드백 생성',
    params: { misconception_id: 'M03', student_name: '김학생' },
  },
  recommend_next_step: {
    desc: '다음 학습 단계 추천',
    params: { current_step: 'LS05', score: 0.45, misconception_ids: ['M01', 'M07'] },
  },
  get_ontology_class: {
    desc: '온톨로지 클래스 정보 조회',
    params: { class_name: 'LinearFunction' },
  },
  generate_practice_problem: {
    desc: '연습 문제 생성',
    params: { step_id: 'LS07', difficulty: 'medium' },
  },
}

export default function MCPConsolePage() {
  const [tools, setTools] = useState<any[]>([])
  const [selectedTool, setSelectedTool] = useState('analyze_student_answer')
  const [paramsText, setParamsText] = useState('')
  const [result, setResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [expandedTool, setExpandedTool] = useState<string | null>(null)

  useEffect(() => {
    mcpApi.getTools().then(res => setTools(res.data.tools || []))
  }, [])

  useEffect(() => {
    const preset = TOOL_PRESETS[selectedTool]
    if (preset) {
      setParamsText(JSON.stringify(preset.params, null, 2))
    }
  }, [selectedTool])

  const handleInvoke = async () => {
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const params = JSON.parse(paramsText)
      const res = await mcpApi.invoke(selectedTool, params)
      setResult(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || '오류 발생')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Cpu size={24} className="text-orange-500" />
          MCP 콘솔
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Model Context Protocol — Agentic AI가 사용하는 7개 수학 학습 툴
        </p>
      </div>

      {/* Protocol Info */}
      <div className="card bg-slate-800 text-white mb-6">
        <div className="flex items-start gap-4">
          <Code size={20} className="text-orange-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">MCP v1.0 — Math Ontology Tool Server</p>
            <p className="text-xs text-slate-400 mt-1">
              Claude MCP, LangChain, AutoGen 등 Agentic AI 프레임워크와 연동 가능한 표준 MCP 인터페이스.
              에이전트가 직접 <code className="bg-slate-700 px-1 rounded">POST /api/v1/mcp/invoke</code>를 호출하여 수학 튜터링 기능을 활용합니다.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Tool List */}
        <div className="lg:col-span-2">
          <div className="card">
            <h3 className="font-semibold text-slate-700 text-sm mb-3">🛠️ 사용 가능한 툴 ({tools.length}개)</h3>
            <div className="space-y-1.5">
              {Object.entries(TOOL_PRESETS).map(([name, { desc }]) => (
                <div key={name}>
                  <button
                    onClick={() => setSelectedTool(name)}
                    className={`w-full text-left p-2.5 rounded-xl text-xs transition-all ${
                      selectedTool === name
                        ? 'bg-orange-100 text-orange-700 border border-orange-200'
                        : 'hover:bg-slate-50 text-slate-700 border border-transparent'
                    }`}
                  >
                    <p className="font-mono font-semibold">{name}</p>
                    <p className="text-slate-500 text-xs mt-0.5">{desc}</p>
                  </button>
                </div>
              ))}
            </div>

            {/* Tool Details */}
            {tools.length > 0 && (
              <div className="mt-4 space-y-1">
                <p className="text-xs font-semibold text-slate-500">툴 스키마</p>
                {tools.filter(t => t.name === selectedTool).map(tool => (
                  <div key={tool.name} className="bg-slate-50 rounded-lg p-3 text-xs">
                    <p className="text-slate-600 mb-2">{tool.description}</p>
                    <div className="space-y-1">
                      {Object.entries(tool.parameters || {}).map(([pname, pinfo]: any) => (
                        <div key={pname} className="flex items-center gap-2">
                          <span className="font-mono text-purple-600">{pname}</span>
                          <span className="text-slate-400">{pinfo.type}</span>
                          {pinfo.required && <span className="badge-red badge text-xs">필수</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Console */}
        <div className="lg:col-span-3 space-y-4">
          {/* Params Editor */}
          <div className="card">
            <h3 className="font-semibold text-slate-700 text-sm mb-2 flex items-center gap-2">
              <span className="badge-orange badge">INVOKE</span>
              <span className="font-mono text-orange-600 text-xs">{selectedTool}</span>
            </h3>
            <textarea
              value={paramsText}
              onChange={e => setParamsText(e.target.value)}
              rows={8}
              className="w-full font-mono text-xs border border-slate-200 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-orange-300 bg-slate-50 resize-none"
              placeholder='{"key": "value"}'
            />
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleInvoke}
                disabled={loading}
                className="btn-primary text-sm"
                style={{ background: loading ? undefined : '#f97316' }}
              >
                <Play size={15} />
                {loading ? '실행 중...' : '툴 실행'}
              </button>
              <button
                onClick={() => setParamsText(JSON.stringify(TOOL_PRESETS[selectedTool]?.params || {}, null, 2))}
                className="btn-secondary text-sm"
              >
                리셋
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="feedback-error">
              <p className="text-sm font-semibold text-red-700">오류 발생</p>
              <p className="text-xs text-red-600 mt-1 font-mono">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
                  <span className="badge-green badge">응답</span>
                  성공
                </h3>
                <span className="text-xs text-slate-400">MCP v1.0</span>
              </div>
              <pre className="bg-slate-50 rounded-xl p-4 text-xs font-mono overflow-auto max-h-96 text-slate-700 whitespace-pre-wrap">
                {JSON.stringify(result.result || result, null, 2)}
              </pre>
            </div>
          )}

          {/* MCP Usage Example */}
          <div className="card bg-slate-800 text-white">
            <h3 className="font-semibold text-sm mb-2 text-slate-300">
              🤖 Claude MCP 연동 예시
            </h3>
            <pre className="text-xs font-mono text-slate-400 whitespace-pre-wrap">
{`# Claude with MCP 사용 예시
import anthropic

client = anthropic.Anthropic()
tools = [
  {
    "name": "${selectedTool}",
    "description": "${TOOL_PRESETS[selectedTool]?.desc}",
    "input_schema": {
      "type": "object",
      "properties": ${JSON.stringify(TOOL_PRESETS[selectedTool]?.params || {}, null, 2)}
    }
  }
]
# 에이전트가 자동으로 툴을 선택하고 호출`}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}
